import Matter from "matter-js";
import * as C from "../../config/constants.js";
import { createBodyForSystem, markFlaskCargoMeta } from "./bodies.js";
import { onFlaskBodySpawned } from "./overflow-state.js";
import { requestContainmentBurst } from "./containment.js";
import { getNextMinimizePhysicsTier } from "./minimize.js";

function getMinimizeMotionMs() {
  var v = C.MINIMIZE_MOTION_MS != null
    ? Number(C.MINIMIZE_MOTION_MS)
    : (C.MERGE_MOTION_MS != null ? Number(C.MERGE_MOTION_MS) : 200);
  return isFinite(v) && v > 0 ? v : 200;
}

function smoothstep(t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

function computePhysicsRadii(system, identityTier, physicsSizeTier) {
  var avgRadius = system.bodyRadiusByTier && system.bodyRadiusByTier[physicsSizeTier] != null
    ? system.bodyRadiusByTier[physicsSizeTier]
    : 10;
  var chain = system.evolutionChain || [];
  var cfg = chain[identityTier] || {};
  var aspect = cfg.ellipseAspect != null
    ? cfg.ellipseAspect
    : (C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT != null ? C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT : 1.0);
  if (Math.abs(aspect - 1) < 0.01) {
    return { radiusX: avgRadius, radiusY: avgRadius };
  }
  var radiusX = (2 * avgRadius * aspect) / (1 + aspect);
  var radiusY = (2 * avgRadius) / (1 + aspect);
  return { radiusX: radiusX, radiusY: radiusY };
}

export function isBodyShrinking(system, bodyId) {
  var list = system && system.activeShrinkMotions;
  if (!list || !list.length) return false;
  for (var i = 0; i < list.length; i++) {
    if (list[i].bodyId === bodyId) return true;
  }
  return false;
}

export function getShrinkDrawRadiiForMeta(system, meta) {
  if (!system || !meta || !meta.body) return null;
  var draw = system.shrinkMotionDraw && system.shrinkMotionDraw[meta.body.id];
  if (!draw) return null;
  return {
    radiusX: draw.drawRadiusX,
    radiusY: draw.drawRadiusY,
  };
}

function applyShrinkBodyReplacement(system, anim) {
  var meta = anim.meta;
  var tier = anim.tier;
  var nextPhysicsTier = anim.nextPhysicsTier;
  var oldBody = meta.body;
  if (!oldBody || !system.bodiesMap[oldBody.id]) return false;

  var World = Matter.World;
  var Body = Matter.Body;
  var pos = { x: oldBody.position.x, y: oldBody.position.y };
  var vel = oldBody.velocity
    ? { x: oldBody.velocity.x || 0, y: oldBody.velocity.y || 0 }
    : { x: 0, y: 0 };
  var oldR = Math.max(anim.oldRadiusX, anim.oldRadiusY, 8);
  var angle = typeof oldBody.angle === "number" ? oldBody.angle : 0;
  var angVel = typeof oldBody.angularVelocity === "number" ? oldBody.angularVelocity : 0;

  World.remove(system.world, oldBody);
  delete system.bodiesMap[oldBody.id];
  system.bodies = system.bodies.filter(function (m) {
    return m !== meta;
  });

  var newMeta = createBodyForSystem(system, tier, pos.x, pos.y, {
    physicsSizeTier: nextPhysicsTier,
    minimized: nextPhysicsTier < tier,
  });
  if (!newMeta || !newMeta.body) return false;

  var newR = Math.max(newMeta.radiusX || 0, newMeta.radiusY || 0, 8);
  var velScale = Math.min(1, Math.max(0.15, newR / oldR));
  vel.x *= velScale;
  vel.y *= velScale;

  markFlaskCargoMeta(newMeta);
  if (typeof Body.setAngle === "function") Body.setAngle(newMeta.body, angle);
  if (typeof Body.setVelocity === "function") Body.setVelocity(newMeta.body, vel);
  if (typeof Body.setAngularVelocity === "function") {
    Body.setAngularVelocity(newMeta.body, angVel);
  }

  onFlaskBodySpawned(newMeta);
  World.add(system.world, newMeta.body);
  system.bodies.push(newMeta);
  system.bodiesMap[newMeta.body.id] = newMeta;
  requestContainmentBurst(system);
  return true;
}

/**
 * @param {object} system FlaskSystem
 * @param {object} meta flask body meta
 */
export function queueShrinkMotion(system, meta) {
  if (!system || !meta || !meta.body) return false;
  if (meta._shrinkAnimating || isBodyShrinking(system, meta.body.id)) return false;

  var nextPhysicsTier = getNextMinimizePhysicsTier(meta);
  var currentPhysicsTier = meta.physicsSizeTier != null
    ? meta.physicsSizeTier
    : (meta.tier != null ? meta.tier : 0);
  if (nextPhysicsTier >= currentPhysicsTier) return false;

  var tier = meta.tier != null ? meta.tier : 0;
  var oldRadiusX = meta.radiusX != null ? meta.radiusX : 10;
  var oldRadiusY = meta.radiusY != null ? meta.radiusY : oldRadiusX;
  var nextRadii = computePhysicsRadii(system, tier, nextPhysicsTier);

  system.activeShrinkMotions = system.activeShrinkMotions || [];
  Matter.Body.setStatic(meta.body, true);
  Matter.Body.setVelocity(meta.body, { x: 0, y: 0 });
  Matter.Body.setAngularVelocity(meta.body, 0);
  meta._shrinkAnimating = true;

  system.activeShrinkMotions.push({
    meta: meta,
    bodyId: meta.body.id,
    tier: tier,
    nextPhysicsTier: nextPhysicsTier,
    oldRadiusX: oldRadiusX,
    oldRadiusY: oldRadiusY,
    newRadiusX: nextRadii.radiusX,
    newRadiusY: nextRadii.radiusY,
    startMs: Date.now(),
    durationMs: getMinimizeMotionMs(),
  });
  return true;
}

export function updateShrinkMotions(system) {
  if (!system || !system.activeShrinkMotions || !system.activeShrinkMotions.length) {
    system.shrinkMotionDraw = null;
    return;
  }

  var now = Date.now();
  var draw = {};
  var remaining = [];

  for (var i = 0; i < system.activeShrinkMotions.length; i++) {
    var anim = system.activeShrinkMotions[i];
    var meta = anim.meta;
    if (!meta || !meta.body || !system.bodiesMap[anim.bodyId]) continue;

    var elapsed = now - anim.startMs;
    var t = Math.min(1, elapsed / anim.durationMs);
    var eased = smoothstep(t);
    var drawRadiusX = anim.oldRadiusX + (anim.newRadiusX - anim.oldRadiusX) * eased;
    var drawRadiusY = anim.oldRadiusY + (anim.newRadiusY - anim.oldRadiusY) * eased;
    var outlineAlpha = t < 0.82 ? 0.72 : 0.72 * (1 - (t - 0.82) / 0.18);

    draw[anim.bodyId] = {
      drawRadiusX: drawRadiusX,
      drawRadiusY: drawRadiusY,
      outlineRadiusX: anim.oldRadiusX,
      outlineRadiusY: anim.oldRadiusY,
      outlineAlpha: outlineAlpha,
    };

    if (t >= 1) {
      meta._shrinkAnimating = false;
      applyShrinkBodyReplacement(system, anim);
    } else {
      remaining.push(anim);
    }
  }

  system.activeShrinkMotions = remaining;
  system.shrinkMotionDraw = remaining.length ? draw : null;
}